const ProductCard = {
  parts: [
    'container',
    'imageContainer',
    'productImage',
    'customization',
    'swatchesCompact',
    'swatchesNamed',
    'colorDots',
    'colorDot',
    'colorDotImage',
    'colorDotSolid',
    'moreSwatches',
    'namedSwatch',
    'namedSwatchImage',
    'namedSwatchColor',
    'swatchName',
    'content',
    'productInfo',
    'textSection',
    'productName',
    'collection',
    'productDetail',
    'separator',
    'priceSection',
    'productPrice',
    'originalPrice',
    'originalPriceValue'
  ],

  baseStyle: {
    container: {
      display: 'flex',
      width: 'full', // Let parent grid control width
      flexDirection: 'column',
      alignItems: 'flex-start',
      position: 'relative',
    },

    imageContainer: {
      width: 'full',
      bg: 'brandColors.primaryLinen15', // Background from design
      position: 'relative',
      overflow: 'hidden',
    },

    productImage: {
      width: 'full',
      objectFit: 'cover',
      objectPosition: 'center',
      aspectRatio: '1/1',
      mixBlendMode: 'multiply',
    },

    customization: {
      display: 'flex',
      width: 'full',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      gap: 1.5, // 6px
      minHeight: {base: '68px', md: '83px'},
      px: {base: 4, md: 6.75},
      pb: {base: 3.5, md: 7},
    },

    swatchesCompact: {
      display: 'flex',
      alignItems: 'center',
      gap: 2, // 8px
      justifyContent: 'center',
      position: 'relative',
      width: 'full',
      flex: '0 0 100%',
      pb: {base: 3.5, md: 2},
    },

    colorDots: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '5px',
      position: 'relative',
    },

    colorDot: {
      display: 'flex',
      width: '12px',
      height: '12px',
      alignItems: 'center',
      gap: 2, // 8px
      aspectRatio: '1/1',
      position: 'relative',
    },

    colorDotImage: {
      flex: '1 0 0',
      alignSelf: 'stretch',
      borderRadius: '3px',
      width: 'full',
      height: 'full',
      objectFit: 'cover',
    },

    colorDotSolid: {
      flex: '1 0 0',
      alignSelf: 'stretch',
      borderRadius: '3px',
      width: 'full',
      height: 'full',
    },

    moreSwatches: {
      color: 'brandColors.primaryDarkLinen', // #8C683B
      textAlign: 'center',
      fontFamily: 'heading',
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: '120%',
    },

    swatchesNamed: {
      display: 'flex',
      alignItems: 'flex-start',
      alignContent: 'flex-start',
      gap: 1.5, // 6px
      flexWrap: 'wrap',
      position: 'relative',
      flexDirection: 'column',
    },

    namedSwatch: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5, // 6px
      position: 'relative',
    },

    namedSwatchImage: {
      width: '14px',
      height: '14px',
      borderRadius: '3px',
    },

    namedSwatchColor: {
      width: '14px',
      alignSelf: 'stretch',
      borderRadius: '3px',
      minHeight: '14px',
    },

    swatchName: {
      color: 'brandColors.greyscale90', // rgba(27, 26, 24, 0.90)
      fontFamily: 'heading',
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: '120%',
    },

    content: {
      display: 'flex',
      padding: 4, // 16px all around on mobile, horizontal only on desktop
      pb: {base: 4, md: 0},
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 4, // 16px
      alignSelf: 'stretch',
      position: 'relative',
    },

    productInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: {base: 4, md: 4}, // 16px consistent
      alignSelf: 'stretch',
      position: 'relative',
    },

    textSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 2, // 8px
      alignSelf: 'stretch',
      position: 'relative',
    },

    productName: {
      alignSelf: 'stretch',
      color: 'brandColors.greyscale90', // rgba(27, 26, 24, 0.90)
      fontFamily: 'heading',
      fontSize: {base: 'md', md: 'lg'}, // 16px mobile, 18px desktop from Figma
      fontWeight: 'medium', // 500
      lineHeight: '120%',
      display: '-webkit-box',
      webkitlineclamp: 2, // Maximum 2 lines as per requirement
      webkitboxorient: 'vertical', // Lowercase vendor prefix
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    collection: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2, // 8px
      alignSelf: 'stretch',
      position: 'relative',
      flexWrap: 'wrap',
    },

    productDetail: {
      color: 'brandColors.greyscale70', // rgba(27, 26, 24, 0.70)
      fontFamily: 'heading',
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: '120%',
    },

    separator: {
      color: 'brandColors.greyscale50', // rgba(27, 26, 24, 0.50)
      fontFamily: 'heading',
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: '120%',
    },

    priceSection: {
      display: 'flex',
      alignItems: 'center',
      gap: 2, // 8px
      position: 'relative',
      flexWrap: 'wrap', // Allow wrapping on smaller screens
    },

    productPrice: {
      color: 'brandColors.greyscale90', // rgba(27, 26, 24, 0.90)
      fontFamily: 'heading',
      fontSize: {base: 'sm', md: 'md'}, // 14px mobile, 16px desktop from Figma
      fontWeight: 'medium', // 500
      lineHeight: '120%',
    },

    originalPrice: {
      color: 'brandColors.greyscale70', // rgba(27, 26, 24, 0.70)
      fontFamily: 'heading',
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: '120%',
      // textDecoration controlled by component prop
    },
    originalPriceValue: {
      pl: 1,
      textDecoration: 'line-through',
    },
  },
};

export default ProductCard;
